import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/lib/supabase'
import type { Song, SongSource } from '@/types'

export const useSongs = (bandId: string | undefined) => {
  const [songs, setSongs] = useState<Song[]>([])
  const [loading, setLoading] = useState(true)

  const fetchSongs = useCallback(async (initial = false) => {
    if (!bandId) return
    if (initial) setLoading(true)
    try {
      const { data, error } = await supabase
        .from('songs')
        .select('*')
        .eq('band_id', bandId)
        .is('deleted_at', null)
        .order('title')
      if (error) throw error
      setSongs(data ?? [])
    } finally {
      if (initial) setLoading(false)
    }
  }, [bandId])

  useEffect(() => {
    void fetchSongs(true)
  }, [fetchSongs])

  // Realtime subscription for live sync
  useEffect(() => {
    if (!bandId) return

    const channel = supabase
      .channel(`songs-${bandId}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'songs', filter: `band_id=eq.${bandId}` }, () => {
        void fetchSongs()
      })
      .subscribe()

    return () => {
      void supabase.removeChannel(channel)
    }
  }, [bandId, fetchSongs])

  const addSong = async (song: {
    title: string
    artist: string
    is_original: boolean
    duration_seconds: number | null
    performance_duration_seconds: number | null
    year: number | null
    source: SongSource
    source_id: string | null
  }) => {
    // Check for soft-deleted duplicate (same source_id or same title+artist)
    if (song.source_id && song.source !== 'original') {
      const { data: existing } = await supabase
        .from('songs')
        .select('id')
        .eq('band_id', bandId!)
        .eq('source_id', song.source_id)
        .not('deleted_at', 'is', null)
        .limit(1)
      if (existing && existing.length > 0) {
        await supabase.from('songs').update({ deleted_at: null, ...song }).eq('id', existing[0].id)
        await fetchSongs()
        return
      }
    }
    const { error } = await supabase.from('songs').insert({ ...song, band_id: bandId })
    if (error) throw error
    await fetchSongs()
  }

  const updateSong = async (id: string, updates: Partial<Pick<Song, 'title' | 'artist' | 'year' | 'performance_duration_seconds' | 'rating'>>) => {
    const { error } = await supabase.from('songs').update(updates).eq('id', id)
    if (error) throw error
    await fetchSongs()
  }

  const removeSong = async (id: string) => {
    const { error } = await supabase.from('songs').update({ deleted_at: new Date().toISOString() }).eq('id', id)
    if (error) throw error
    await fetchSongs()
  }

  return { songs, loading, refetch: fetchSongs, addSong, updateSong, removeSong }
}
